import type { PlatformName } from '../api/types.js';

/**
 * Platform brand mark (ARCHITECTURE.md §65).
 *
 * DECORATIVE BY DEFAULT. Where the platform's name sits beside it in text, the
 * icon carries no information of its own — `alt=""` and `aria-hidden` keep a
 * screen reader from announcing "Twitch Twitch".
 *
 * Pass `label` where the icon is the ONLY thing identifying the platform, as it
 * is on each row of the chat feed. It then gets a real accessible name and
 * stops being hidden. §65's rule is that meaning must never rest on colour
 * alone; an icon is a shape as well as a colour, and with an accessible name it
 * is a label rather than a decoration. Without one it would be neither, which
 * is why this is an explicit opt-in rather than something a call site can
 * forget its way into.
 *
 * Assets are served from `public/platforms/` as same-origin files, which the
 * CSP already permits (`img-src 'self'`). No external CDN, no inline SVG from a
 * provider — both would be ways for a third party to place content on a page
 * that renders untrusted chat.
 */

/**
 * Which platforms ship an asset, and in what format.
 *
 * TikTok uses the LIGHT variant: its standard mark is dark-on-transparent and
 * would disappear against Connaro's dark surfaces.
 *
 * Kick deliberately uses the STANDARD (dark) mark. Its black tile does sit
 * close to the card surface, but that is Kick's actual brand treatment and it
 * reads as intended — a chosen look, not an oversight to be "fixed" back to the
 * light variant later.
 *
 * Every mark is an SVG again. Rumble shipped as a PNG for a while — the only
 * mark available was a 112x118 raster, and tracing one would have meant
 * deciding the outline of somebody else's logo, which is the same objection as
 * inventing it. A real vector was supplied, so it follows X and Blaze back to
 * the vector set. Compared against the raster before the swap: same shape,
 * same #85c742, and crisp where the raster blurred.
 *
 * A raster would not be a silent downgrade if one arrives again:
 * `platform-icon-contrast.test.ts` decodes PNGs as well as SVGs, so every
 * shipped mark is held to the same floor whatever its format.
 *
 * Marks are trimmed to their bounding box, so most are not square (Blaze is
 * 386x512, Twitch 449x512). `object-fit: contain` letterboxes them inside the
 * square box `size` sets.
 *
 * Blaze once had no asset at all and fell back to the monogram below, on the
 * reasoning that inventing a brand mark for someone else's product is worse
 * than having none. That still holds — its mark was supplied, not invented.
 */
const ICONS: Partial<Record<PlatformName, string>> = {
  twitch: '/platforms/twitch.svg',
  youtube: '/platforms/youtube.svg',
  facebook: '/platforms/facebook.svg',
  kick: '/platforms/kick.svg',
  blaze: '/platforms/blaze.svg',
  discord: '/platforms/discord.svg',
  tiktok: '/platforms/tiktok.svg',
  x: '/platforms/x.svg',
  rumble: '/platforms/rumble.svg',
  instagram: '/platforms/instagram.svg',
};

export type PlatformIconProps = {
  platform: PlatformName;
  /** Rendered name, used for the monogram fallback. */
  displayName: string;
  /** Pixel size. Sets a square box; a non-square mark is contained within it. */
  size?: number;
  /**
   * Accessible name, when the icon is the only identification of the platform.
   *
   * Omit it wherever the name is already beside the icon in text — announcing
   * it twice is worse than not announcing it at all.
   */
  label?: string;
};

/**
 * Marks are always full colour, including for platforms that cannot be
 * connected.
 *
 * An earlier version dimmed those. It was defensible — §18 wants no implied
 * support — but the message was already carried better by the status pill and
 * the disabled button, both of which say it in words. Desaturating the brand
 * mark as well made the card look broken rather than pending, and cost
 * recognisability for no added meaning.
 */
export function PlatformIcon({
  platform,
  displayName,
  size = 28,
  label,
}: PlatformIconProps): React.JSX.Element {
  const source = ICONS[platform];
  const className = 'platform-icon';
  const meaningful = label !== undefined && label !== '';

  if (source === undefined) {
    /*
     * No asset. A monogram in the platform's own initial, rather than a broken
     * image or a generic placeholder that would read as "unknown platform".
     *
     * A span carrying meaning needs `role="img"` as well as a name: without the
     * role, assistive technology treats it as the letter it contains, and a
     * name like "Blaze" would be announced as "B".
     *
     * Every platform in `PlatformName` currently ships an asset, so nothing
     * reaches this branch today. It stays because the next platform added will
     * land here before its mark does, and a monogram is a better first
     * impression than a broken image. Rumble spent a while here for exactly
     * that reason.
     */
    return (
      <span
        className={`${className} platform-icon--monogram`}
        style={{ width: size, height: size }}
        {...(meaningful ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      >
        {displayName.slice(0, 1).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      className={className}
      src={source}
      width={size}
      height={size}
      /*
       * `alt` carries the platform name only when nothing else does. An empty
       * alt on a decorative image is correct and deliberate — it is what stops
       * a screen reader reading the name twice where text already says it.
       */
      alt={meaningful ? label : ''}
      {...(meaningful ? {} : { 'aria-hidden': true })}
      loading="lazy"
      decoding="async"
    />
  );
}
